import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";

import { apiFormat } from "@similarweb/pro-logic-components";
import Sentry from "@similarweb/sentry-logger";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";
import { KeywordOverviewCompetitiveStats } from "app/components/widget/widget-config/metrics/keyword-metrics";
import {
    DEFAULT_SORT,
    STATS_COMPOSITION_ENDPOINT,
} from "app/common/keyword-research/keywords-overview/serpCompositionConsts";
import type { IStatsResponseData } from "app/common/keyword-research/keywords-overview/serpCompositionTypes";
import type { SerpMetricRowData } from "app/common/keyword-research/keywords-overview/serpMetricsConsts";
import { transformSerpMetricsData } from "app/common/keyword-research/keywords-overview/serpMetricsUtils";
import type {
    IClicksRowData,
    IVolumeAndNoClicksRowData,
} from "app/common/keyword-research/keywords-overview/volumeAndClicksTypes";
import {
    fetchData,
    getApiParams,
    transformClicksData,
    transformVolumeAndNoClicksData,
} from "app/common/keyword-research/keywords-overview/volumeAndClicksUtils";
import { DefaultFetchService } from "app/services/fetchService";

import { SingleMetricWidget } from "../SingleMetricWidget";

const getCompetitiveStatsApiParams = ({ keys, from, to, country, webSource, isWindow }) => {
    return {
        webSource,
        Device: webSourceToDeviceMap[webSource],
        includeSubDomains: true,
        sort: DEFAULT_SORT,
        asc: false,
        isWindow,
        country,
        from,
        to,
        key: keys,
    };
};

const onSuccessfullyFetchedCompetitiveStatsData = (res) => {
    const transformedSerpMetricsData = transformSerpMetricsData(res);
    return { Data: transformedSerpMetricsData };
};

const onSuccessfullyFetchedPerformanceStatsData =
    (keys, fromAsDayjs, webSource, showChange) =>
    ({ volumeAndNoClicksData, clicksData }) => {
        const transformedVolumeAndNoClicksData = transformVolumeAndNoClicksData(
            volumeAndNoClicksData,
            keys,
            fromAsDayjs,
            webSource,
            showChange,
        );
        const transformedClicksData = transformClicksData(clicksData, keys, webSource, fromAsDayjs, showChange);
        return {
            Data: { ...transformedVolumeAndNoClicksData, ...transformedClicksData },
        };
    };

type KeywordsOverviewStatsDisplayData = {
    Data: SerpMetricRowData | (IVolumeAndNoClicksRowData & IClicksRowData) | object;
};

export class KeywordOverviewStatsWidget extends SingleMetricWidget {
    static getWidgetResourceType() {
        return "SingleMetric";
    }

    static getWidgetDashboardType() {
        return "SingleMetric";
    }

    static getWidgetMetadataType() {
        return "KeywordOverviewStats";
    }

    protected validateData(
        response: SerpMetricRowData | (IVolumeAndNoClicksRowData & IClicksRowData) | object,
    ): boolean {
        return !isEmpty(response);
    }

    private dataFetcherFactory = () => {
        return {
            create: (widget) => {
                return {
                    async fetch(): Promise<KeywordsOverviewStatsDisplayData> {
                        if (widget.apiParams.metric === KeywordOverviewCompetitiveStats.id) {
                            const competitiveStatsApiParams = getCompetitiveStatsApiParams(widget.apiParams);
                            return DefaultFetchService.getInstance()
                                .get<IStatsResponseData>(STATS_COMPOSITION_ENDPOINT, competitiveStatsApiParams)
                                .then(onSuccessfullyFetchedCompetitiveStatsData)
                                .catch((e) => {
                                    Sentry.error(
                                        "Dashboard Keyword Overview Stats data fething - Error fetching data:",
                                        e,
                                    );
                                    throw e;
                                });
                        } else {
                            const { keys, from, to, country, isWindow, webSource } = widget.apiParams;

                            const fromAsDayjs = dayjs(from, apiFormat);
                            const showChange = !isWindow;
                            const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(keys);
                            const keysForApi = isKeywordGroup ? keys.substring(1) : keys;

                            const { volumeAndNoClicksParams, clicksParams } = await getApiParams(
                                isKeywordGroup,
                                keysForApi,
                                { from, to, isWindow },
                                country,
                                webSource,
                            );

                            return fetchData(volumeAndNoClicksParams, clicksParams)
                                .then(
                                    onSuccessfullyFetchedPerformanceStatsData(
                                        keysForApi,
                                        fromAsDayjs,
                                        webSource,
                                        showChange,
                                    ),
                                )
                                .catch((e) => {
                                    Sentry.error(
                                        "Dashboard Keyword Overview Stats data fething - Error fetching data:",
                                        e,
                                    );
                                    throw e;
                                });
                        }
                    },
                };
            },
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    callbackOnGetData(response: KeywordsOverviewStatsDisplayData): void {
        this.data = response.Data;
    }
}

KeywordOverviewStatsWidget.register();
